import { Component, Inject, LOCALE_ID, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { concatMap, filter, finalize, map, switchMap } from 'rxjs/operators';
import { CreateDocFailedResponseDto } from 'src/app/data-transfer/entities/doc-entities/create-doc-failed-response-dto';
import { CreateDocPreRequestDto } from 'src/app/data-transfer/entities/doc-entities/create-doc-pre-request-dto';
import { CreateDocRequestDto } from 'src/app/data-transfer/entities/doc-entities/create-doc-request-dto';
import { CreateDocTdRequestDto } from 'src/app/data-transfer/entities/doc-entities/create-doc-td-request';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { OverviewTableComponent } from '../overview-table/overview-table.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { CreateDocDialogComponent } from 'src/app/components/dialogs/create-doc-dialog/create-doc-dialog.component';
import { CreateDocSignersDialogComponent } from 'src/app/components/dialogs/create-doc-signers-dialog/create-doc-signers-dialog.component';
import { CreateDocFailedDialogComponent, CreateDocFailedDialogData } from 'src/app/components/dialogs/create-doc-failed-dialog/create-doc-failed-dialog.component';
import { CreateDocTdDownloadDialogComponent } from 'src/app/components/dialogs/create-doc-td-download-dialog/create-doc-td-download-dialog.component';
import { DirectoryApiService } from 'src/app/data-transfer/services/directory-api-service';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { ParentNavigationService } from 'src/app/navigation/services/navigation-services/parent-navigation-service';
import { TranslocoService } from '@jsverse/transloco';
import { DialogActions } from 'src/app/model/dictionary';
import { SimpleAlertDialogComponent } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { CreateDocTdDialogComponent } from 'src/app/components/dialogs/create-doc-td-dialog/create-doc-td-dialog.component';
import { CreateDocSignatureDialogComponent } from 'src/app/components/dialogs/create-doc-signature-dialog/create-doc-signature-dialog.component';

@Component({
  selector: 'app-doc-capable-overview-table',
  templateUrl: './doc-capable-overview-table.component.html',
  styleUrls: ['./doc-capable-overview-table.component.scss'],
})
export abstract class DocCapableOverviewTableComponent extends OverviewTableComponent implements OnDestroy {
  private docProblemsSubscription?: Subscription;
  private docWizardSubscription?: Subscription;
  private docGenerationSubscription?: Subscription;
  private technicalDocSubscription?: Subscription;

  constructor(
    dialog: MatDialog,
    directoryApiService: DirectoryApiService,
    importExportApiService: ImportExportApiService,
    navigationService: ParentNavigationService,
    translateService: TranslocoService,
    protected userApiService: UserApiService,
    protected spinner: NgxSpinnerService,
    @Inject(LOCALE_ID) locale: string
  ) {
    super(dialog, directoryApiService, importExportApiService, navigationService, translateService, spinner, locale);
  }

  protected abstract isPackagingSystem(): boolean;
  protected abstract getSelectedIds(): number[];
  protected abstract fetchDocProblems(requestDto: CreateDocTdRequestDto): Observable<CreateDocFailedResponseDto[]>;
  protected abstract generateDocBlob(requestDto: CreateDocRequestDto): Observable<Blob>;
  protected abstract generateTechnicalDocBlob(requestDto: CreateDocTdRequestDto): Observable<Blob>;
  public abstract resetSelection(): void;

  createDoc(): void {
    const selectedIds = this.getSelectedIds();
    const packagingElements: CreateDocPreRequestDto[] = selectedIds.map(id => ({ id }));

    this.docProblemsSubscription = this.dialog
      .open(CreateDocTdDialogComponent, getDialogConfig({ mode: 'doc', packagingElements }, '600px'))
      .afterClosed()
      .pipe(
        filter(result => !!result?.confirmed),
        switchMap(result => {
          const requestDto: CreateDocTdRequestDto = {
            language: result.language,
            packagingElements,
          };
          return this.fetchDocProblems(requestDto).pipe(map(problemEntries => ({ problemEntries, requestDto })));
        })
      )
      .subscribe({
        next: ({ problemEntries, requestDto }) => {
          if (problemEntries.length === 0) {
            this.startDocWizard(requestDto);
          } else {
            const failedDialogData: CreateDocFailedDialogData = {
              totalCount: packagingElements.length,
              problemEntries,
              isPackagingSystem: this.isPackagingSystem(),
              language: requestDto.language,
            };

            this.dialog
              .open(CreateDocFailedDialogComponent, getDialogConfig(failedDialogData, '750px'))
              .afterClosed()
              .subscribe(result => {
                if (result?.event === DialogActions.CONFIRM) {
                  this.startDocWizard(requestDto);
                }
                this.resetSelection();
              });
          }
        },
        error: () => this.openErrorDialog('createDoc.errors.problemCheckFailed'),
      });
  }

  protected startDocWizard(requestDto: CreateDocTdRequestDto): void {
    this.spinner.show();

    this.docWizardSubscription = this.userApiService
      .getOwnOrganization()
      .pipe(
        concatMap(org => {
          this.spinner.hide();
          return this.dialog
            .open(
              CreateDocDialogComponent,
              getDialogConfig(
                {
                  manufacturers: org.manufacturerProfiles ?? [],
                  representatives: org.authorizedRepresentativeProfiles ?? [],
                },
                '600px'
              )
            )
            .afterClosed()
            .pipe(map(createDocResult => ({ org, createDocResult })));
        }),
        filter(ctx => !!ctx.createDocResult?.confirmed),
        concatMap(ctx =>
          this.dialog
            .open(
              CreateDocSignersDialogComponent,
              getDialogConfig(
                {
                  managingDirectors: ctx.org.ceoProfiles ?? [],
                  signers: ctx.org.signeeProfiles ?? [],
                },
                '600px'
              )
            )
            .afterClosed()
            .pipe(map(signeeResult => ({ ...ctx, signeeResult })))
        ),
        filter(ctx => !!ctx.signeeResult?.confirmed),
        concatMap(ctx =>
          this.dialog
            .open(CreateDocSignatureDialogComponent, getDialogConfig({}, '600px'))
            .afterClosed()
            .pipe(map(signatureResult => ({ ...ctx, signatureResult })))
        ),
        filter(ctx => !!ctx.signatureResult?.confirmed),
        map(ctx => ({
          manufacturerId: ctx.createDocResult.manufacturerId,
          authorizedRepresentativeId: ctx.createDocResult.authorizedRepresentativeId,
          ceoId: ctx.signeeResult.ceoId,
          signeeId: ctx.signeeResult.signeeId,
          language: requestDto.language,
          packagingElements: requestDto.packagingElements,
        })),
        finalize(() => this.resetSelection())
      )
      .subscribe({
        next: finalRequestDto => this.generateDoc(finalRequestDto),
        error: () => {
          this.spinner.hide();
          this.openErrorDialog('createDoc.errors.wizardDataLoadFailed');
        },
      });
  }

  protected generateDoc(requestDto: CreateDocRequestDto): void {
    this.spinner.show();

    this.docGenerationSubscription = this.generateDocBlob(requestDto).subscribe({
      next: blob => {
        this.spinner.hide();
        this.openDocDownloadDialog(blob, requestDto.packagingElements.length, true);
      },
      error: () => {
        this.spinner.hide();
        this.openErrorDialog('createDoc.errors.docGenerationFailed');
      },
    });
  }

  createTechnicalDoc(): void {
    const selectedIds = this.getSelectedIds();
    const packagingElements: CreateDocPreRequestDto[] = selectedIds.map(id => ({ id }));

    this.technicalDocSubscription = this.dialog
      .open(CreateDocTdDialogComponent, getDialogConfig({ mode: 'td', packagingElements }, '600px'))
      .afterClosed()
      .pipe(
        filter(result => !!result?.confirmed),
        switchMap(result => {
          this.spinner.show();
          const requestDto: CreateDocTdRequestDto = {
            language: result.language,
            packagingElements,
          };
          return this.generateTechnicalDocBlob(requestDto);
        }),
        finalize(() => this.resetSelection())
      )
      .subscribe({
        next: blob => {
          this.spinner.hide();
          this.openDocDownloadDialog(blob, packagingElements.length, false);
        },
        error: () => {
          this.spinner.hide();
          this.openErrorDialog('createDoc.errors.tdGenerationFailed');
        },
      });
  }

  private openDocDownloadDialog(blob: Blob, fileCount: number, isDoc: boolean): void {
    const prefix = isDoc ? 'DoC_PPWR_' : 'TD_PPWR_';
    this.dialog.open(
      CreateDocTdDownloadDialogComponent,
      getDialogConfig(
        {
          isDoc,
          blob,
          fileName: `${prefix}${new Date().toISOString().slice(0, 10)}.zip`,
          fileCount,
          fileSizeMb: blob?.size ? blob.size / (1024 * 1024) : 0,
        },
        '480px'
      )
    );
  }

  private openErrorDialog(translationKey: string): void {
    this.dialog.open(
      SimpleAlertDialogComponent,
      getDialogConfig(
        {
          title: this.translateService.translate('common.text.error'),
          messages: [this.translateService.translate(translationKey)],
          icon: 'error',
        },
        '500px'
      )
    );
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.docProblemsSubscription?.unsubscribe();
    this.docWizardSubscription?.unsubscribe();
    this.docGenerationSubscription?.unsubscribe();
    this.technicalDocSubscription?.unsubscribe();
  }
}
